import { css, type Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { createClassNames } from '@hrworks/design-system/functions';
import { overflowBreakWord, shouldForwardProp } from '@hrworks/design-system/utilities';

import { Button, type ButtonProps, getButtonColorMap } from '../../../Button';
import { TitleStyles } from '../../../Title';
import { S as CollapsibleMenuSectionStyles } from '../../Section/CollapsibleMenuSection.styles';
import { S as CollapsibleMenuEntryStyles } from '../CollapsibleMenuEntry.styles';

type PaddingGenerationConfig = {
  theme: Theme;
  iconFontSize?: string;
  iconGap?: string;
  horizontalItemPadding?: string;
  nestedInset?: string;
};

export const generatePaddings = ({
  theme,
  iconFontSize,
  iconGap,
  horizontalItemPadding,
  nestedInset,
}: PaddingGenerationConfig) => {
  const _iconFontSize = iconFontSize || theme.eclipseTier2Typography.bodyMd.fontSize;
  const _iconGap = iconGap || theme.eclipseTier1.spacing['12px'];
  const iconPadding = `${_iconFontSize} + ${_iconGap}`;
  const _horizontalItemPadding = horizontalItemPadding || theme.eclipseTier1.spacing['12px'];
  const _nestedInset = nestedInset || theme.eclipseTier1.spacing['12px'];
  const paddingWithIcon = `calc(${iconPadding} + ${_horizontalItemPadding})`;
  const defaultNestingIndentation = `calc(${_nestedInset} * var(--nestingLevel) + ${_horizontalItemPadding})`;
  const nestedIndentationWithIcon = `calc(${_nestedInset} * (var(--nestingLevel) - 1) + ${paddingWithIcon})`;

  return css({
    [sel.IconContainer]: {
      fontSize: _iconFontSize,
      marginRight: _iconGap,
    },

    [`${sel.Item}, ${sel.ItemContainer} ${TitleStyles.sel.TitleContainer}, ${CollapsibleMenuSectionStyles.sel.Section} ${TitleStyles.sel.TitleContainer}`]:
      {
        transition: 'none',
        paddingLeft: _horizontalItemPadding,
        '& > :last-child': {
          marginRight: _horizontalItemPadding,
          [`&${CollapsibleMenuEntryStyles.sel.IconContainer}`]: {
            paddingRight: _horizontalItemPadding,
            marginRight: 0,
          },
        },
      },

    // Inset Layers when Layer0 has no icon
    [`${sel.ItemContainer}:not(${sel.ItemContainer} ${sel.ItemContainer})`]: {
      // Set base line position for Layer0
      '--line-position-depth-indicator': _horizontalItemPadding,

      [` ${sel.ItemContainer} ${sel.Item}, ${TitleStyles.sel.TitleContainer}`]: {
        paddingLeft: defaultNestingIndentation,
      },
      // Set line position for children
      [sel.ItemContainer]: {
        '--line-position-depth-indicator': defaultNestingIndentation,
      },
    },

    // Inset Layer1 when Layer0 has an icon
    [`${sel.ItemContainer}[data-hasicon=true]:not(${sel.ItemContainer} ${sel.ItemContainer})`]: {
      // Set line position in the center of icon for Layer0
      '--line-position-depth-indicator': `calc(${_horizontalItemPadding} + (${_iconFontSize} / 2) - ${
        CollapsibleMenuEntryStyles.componentConfig.indicatorWidth / 2
      }px)`,

      [`${sel.ItemContainer} ${sel.Item}, ${TitleStyles.sel.TitleContainer}`]: {
        paddingLeft: paddingWithIcon,
      },
      // Set the line position for Layer 1
      [sel.ItemContainer]: {
        '--line-position-depth-indicator': paddingWithIcon,
        //Inset Layer 2 even more
        [`${sel.ItemContainer} ${sel.Item},${TitleStyles.sel.TitleContainer}`]: {
          paddingLeft: nestedIndentationWithIcon,
        },
        // Update line position for deeper nesting
        [sel.ItemContainer]: {
          '--line-position-depth-indicator': nestedIndentationWithIcon,
        },
      },
    },
  });
};

const ItemContainer = styled.li();

type ItemProps = {
  $active: boolean;
  $showDefaultCursor: boolean;
  variant: Extract<ButtonProps['variant'], 'filled' | 'subtle' | 'ghost'>;
  color: NonNullable<ButtonProps['color']>;
};

const Item = styled(Button, {
  shouldForwardProp,
})<ItemProps>(({ theme, $active, $showDefaultCursor, color, variant }) => ({
  ...theme.eclipseTier2Typography.bodyMd,
  color: theme.eclipseTier2Color.text.default,
  padding: 0,
  width: '100%',
  outlineOffset: -2,
  minHeight: 40,
  whiteSpace: 'normal',
  ...($showDefaultCursor && {
    cursor: 'default',
  }),
  ...($active && {
    backgroundColor: getButtonColorMap(theme)[variant][color].pressed,
  }),
}));

const LinkContent = styled.div([
  overflowBreakWord,
  {
    display: 'flex',
    alignItems: 'center',
    flex: '1',
    textAlign: 'left',
  },
]);

const IconContainer = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: 'currentcolor',
  width: '1em',
});

const components = { ItemContainer, Item, LinkContent, IconContainer };
const { cls, sel } = createClassNames('CollapsibleMenuBaseItem', components);

export const S = {
  ...components,
  generatePaddings,
  cls,
  sel,
} as const;
